import React, {
  useEffect,
  useRef,
  forwardRef,
  useState,
  useImperativeHandle,
} from 'react'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import TextField from './TextField'
import dayjs from 'dayjs'
import useResponsiveView from '../../../hooks/useResponsiveView'
import * as locales from 'react-day-picker/locale'
import { useWatch } from 'react-hook-form'

export const DatePicker = forwardRef(
  (
    {
      name,
      label,
      control,
      setValue,
      disableBefore,
      numberOfMonths = 2,
      disableAfter,
      error,
      disabled = false,
      dictionary,
      defaultValue,
      onChange,
    },
    ref
  ) => {
    const [date, setDate] = useState(defaultValue)
    const [currentMonth, setCurrentMonth] = useState(new Date())
    const [openPicker, setOpenPicker] = useState(false)
    const [calendarPosition, setCalendarPosition] = useState('center')
    const [isPositioned, setIsPositioned] = useState(false)
    const pickerRef = useRef(null) // Add ref for calendar container
    const calendarRef = useRef(null)
    const { isTablet, isMobile } = useResponsiveView()
    const formValue = useWatch({ control, name })
    const locale = document.documentElement.lang
      ? locales[document.documentElement.lang]
      : locales['el']

    const disablePrevMonth =
      currentMonth.getFullYear() === disableBefore?.getFullYear() &&
      currentMonth.getMonth() === disableBefore?.getMonth()
    const disableNextMonth =
      currentMonth.getFullYear() === disableAfter?.getFullYear() &&
      currentMonth.getMonth() === disableAfter?.getMonth()

    // Sync internal date state with form value changes
    useEffect(() => {
      if (formValue) {
        const parsedDate = new Date(formValue)
        setDate(parsedDate)
      }
    }, [formValue])

    useEffect(() => {
      if (openPicker) {
        // Reset positioned state when opening
        setIsPositioned(false)
      }
    }, [openPicker])

    useEffect(() => {
      if (
        openPicker &&
        !isPositioned &&
        calendarRef.current &&
        pickerRef.current
      ) {
        const calculatePosition = () => {
          const calendar = calendarRef.current
          const container = pickerRef.current
          if (!calendar || !container) return

          const containerRect = container.getBoundingClientRect()
          const calendarRect = calendar.getBoundingClientRect()

          const viewportWidth = window.innerWidth

          // Calculate positions
          const centerLeft =
            containerRect.left +
            containerRect.width / 2 -
            calendarRect.width / 2
          const rightEdge = centerLeft + calendarRect.width

          // Check if calendar would be cut off on the left
          if (centerLeft < 0) {
            setCalendarPosition('left')
          }
          // Check if calendar would be cut off on the right
          else if (rightEdge > viewportWidth) {
            setCalendarPosition('right')
          }
          // Default to center
          else {
            setCalendarPosition('center')
          }

          // Mark as positioned to show calendar
          setIsPositioned(true)
        }

        // Calculate position after calendar renders
        requestAnimationFrame(() => {
          calculatePosition()
        })
      }
    }, [openPicker, isPositioned])

    useEffect(() => {
      if (
        openPicker &&
        isPositioned &&
        calendarRef.current &&
        pickerRef.current
      ) {
        const calculatePosition = () => {
          const calendar = calendarRef.current
          const container = pickerRef.current
          if (!calendar || !container) return

          const containerRect = container.getBoundingClientRect()
          const calendarRect = calendar.getBoundingClientRect()

          const viewportWidth = window.innerWidth

          const centerLeft =
            containerRect.left +
            containerRect.width / 2 -
            calendarRect.width / 2
          const rightEdge = centerLeft + calendarRect.width

          if (centerLeft < 0) {
            setCalendarPosition('left')
          } else if (rightEdge > viewportWidth) {
            setCalendarPosition('right')
          } else {
            setCalendarPosition('center')
          }
        }

        // Recalculate on window resize
        window.addEventListener('resize', calculatePosition)

        return () => {
          window.removeEventListener('resize', calculatePosition)
        }
      }
    }, [openPicker, isPositioned])

    useEffect(() => {
      if (disableBefore) {
        setCurrentMonth(disableBefore)
      }
    }, [disableBefore])

    useEffect(() => {
      const handleBodyClick = (event) => {
        if (
          pickerRef.current &&
          !pickerRef.current.contains(event.target) &&
          !event.target.closest('.b-datepicker__input-container')
        ) {
          setOpenPicker(false)
        }
      }

      document.body.addEventListener('click', handleBodyClick)

      return () => {
        document.body.removeEventListener('click', handleBodyClick)
      }
    })

    const handleSelectDate = (date) => {
      // If date is undefined, it means user clicked the same date (react-day-picker tries to unselect)
      // In this case, prevent unselection and keep the flow going
      if (!date) {
        // Close picker and trigger onChange with current value to proceed to next field
        if (!isMobile) {
          setOpenPicker(false)
          if (typeof onChange === 'function' && formValue) {
            onChange(formValue)
          }
        }
        return
      }

      const formattedDate = dayjs(date).format('YYYY-MM-DD')
      const currentFormattedDate = formValue
        ? dayjs(formValue).format('YYYY-MM-DD')
        : null

      // Check if the selected date is different from the current date
      const isDifferentDate = formattedDate !== currentFormattedDate

      // Only update the value if it's a different date
      if (isDifferentDate) {
        setValue(name, formattedDate, { shouldValidate: true })
        setDate(date)
      }

      // On desktop, close picker and trigger onChange
      if (!isMobile) {
        setOpenPicker(false)
        // Call parent's onChange if it exists (even if same date, to keep the flow)
        if (typeof onChange === 'function') {
          onChange(formattedDate)
        }
      }
      // On mobile, keep picker open for confirmation
    }

    const handlePrevMonth = () => {
      if (disablePrevMonth) return
      setCurrentMonth((prevMonth) => {
        const newMonth = new Date(prevMonth) // Create a new Date object
        newMonth.setMonth(newMonth.getMonth() - 1)
        return newMonth
      })
    }

    const handleNextMonth = () => {
      if (disableNextMonth) return
      setCurrentMonth((prevMonth) => {
        const newMonth = new Date(prevMonth) // Create a new Date object
        newMonth.setMonth(newMonth.getMonth() + 1)
        return newMonth
      })
    }

    // 👇 expose open() and close() to parent
    useImperativeHandle(ref, () => ({
      open: () => setOpenPicker(true),
      close: () => setOpenPicker(false),
    }))

    return (
      <div className="b-date-picker" ref={pickerRef}>
        <TextField
          type="date"
          name={name}
          id={name}
          label={label}
          control={control}
          onClick={() => setOpenPicker((old) => !old)}
          error={error}
          disabled={disabled}
        />
        {openPicker && (
          <div
            className={`b-date-picker__calendar ${calendarPosition} ${
              isPositioned ? 'positioned' : ''
            }`}
            ref={calendarRef}>
            <div className="b-date-picker__nav">
              <i
                className={`icon icon-arrow-left ${
                  disablePrevMonth ? 'disabled' : ''
                }`}
                onClick={handlePrevMonth}></i>
              <i
                className={`icon icon-arrow-right ${
                  disableNextMonth ? 'disabled' : ''
                }`}
                onClick={handleNextMonth}></i>
            </div>

            <DayPicker
              mode="single"
              selected={date}
              onSelect={handleSelectDate}
              weekStartsOn={1}
              numberOfMonths={isTablet || isMobile ? 1 : numberOfMonths}
              locale={locale}
              month={currentMonth}
              disabled={{
                before: disableBefore,
                after: disableAfter,
              }}
              footer={
                <div className="b-date-picker__footer">
                  {isMobile ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-full-width"
                      onClick={() => {
                        setOpenPicker(false)
                        // Trigger onChange on mobile confirmation (even if same date, to keep the flow)
                        if (typeof onChange === 'function' && date) {
                          const formattedDate = dayjs(date).format('YYYY-MM-DD')
                          onChange(formattedDate)
                        }
                      }}>
                      {dictionary.confirm}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => setOpenPicker(false)}>
                      {dictionary.cancel}
                    </button>
                  )}
                </div>
              }
            />
          </div>
        )}
      </div>
    )
  }
)
